<template>
  <div class="m-pagination" :class="paginationClass">
    <!-- Summary: свой ряд — не зависим от слотов/grid Prime Paginator -->
    <div v-if="variant === 'summary'" class="m-pagination__summary">
      <span v-if="rowCounterText" class="m-pagination__start-text m-pagination__row-counter">
        Всего строк:
        <strong v-if="selectedCount > 0">{{ selectedCount }}/</strong><strong>{{ totalRecordsText }}</strong>
      </span>
      <span v-else class="m-pagination__start-text">{{ startText }}</span>
      <div class="m-pagination__rpp">
        <span class="m-pagination__rpp-label">Показывать:</span>
        <Select
          appendTo="#ui-popup-root"
          :model-value="pageSize"
          :options="rowsPerPageOptions"
          :disabled="totalRecords <= 0"
          class="m-pagination__rpp-select"
          :pt="rppSelectPt"
          @update:model-value="onRowsChange"
        />
      </div>
    </div>

    <Paginator
      v-else
      :key="paginatorSyncKey"
      always-show
      class="m-pagination__paginator"
      :rows="pageSize"
      :total-records="totalRecords"
      :first="firstRecord"
      :page-link-size="7"
      template="PrevPageLink PageLinks NextPageLink"
      @page="onPageChange"
    >
      <template #start>
        <span class="m-pagination__start-text">{{ startText }}</span>
        <slot name="start" />
      </template>
      <template #end>
        <slot name="end" />
      </template>
    </Paginator>
  </div>
</template>

<script setup lang="ts">
import { formatTableRowCounterText } from '@/common/utils/formatTableRowCounterText'
import { buildStableTestId } from '@/common/utils/testId'
import { computed } from 'vue'
import Paginator from 'primevue/paginator'
import Select from 'primevue/select'

export type MPaginationVariant = 'summary' | 'footer'

interface PaginationValue {
  page: number
  pages: number
  itemsPerPage?: number
  total?: number
  totalItemsBackEnd?: number
}

interface Props {
  modelValue: PaginationValue
  elementId?: string | number | null
  /** summary — сверху (всего + page size); footer — снизу (диапазон + страницы) */
  variant?: MPaginationVariant
  /** selected/total counter */
  selectedCount?: number | null
  rowsPerPageOptions?: number[]
}

const props = withDefaults(defineProps<Props>(), {
  elementId: null,
  variant: 'footer',
  selectedCount: undefined,
  rowsPerPageOptions: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: PaginationValue]
  'change': [value: PaginationValue]
}>()

const defaultRowsPerPageOptions = [5, 10, 20, 50, 100]
const rowsPerPageOptions = computed(() =>
  props.rowsPerPageOptions?.length ? props.rowsPerPageOptions : defaultRowsPerPageOptions,
)

const paginationClass = computed(() => `m-pagination--${props.variant}`)

const rppSelectPt = computed(() => {
  const id = props.elementId
  if (id == null || String(id).trim() === '') return undefined
  const listboxTestId = buildStableTestId('element', [id, 'listbox'])
  return {
    listContainer: { 'data-testid': listboxTestId },
  }
})

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const pageSize = computed(() => {
  const parsed = toFiniteNumber(props.modelValue.itemsPerPage)
  return parsed && parsed > 0 ? parsed : 10
})

const exactTotalRecords = computed(() => {
  const model = props.modelValue as any
  return (
    toFiniteNumber(model.totalItemsBackEnd) ??
    toFiniteNumber(model.totalItems) ??
    toFiniteNumber(props.modelValue.total)
  )
})

const totalRecords = computed(() => {
  const fallbackPages = toFiniteNumber(props.modelValue.pages) ?? 1
  return exactTotalRecords.value ?? Math.max(0, fallbackPages * pageSize.value)
})

const totalRecordsText = computed(() => {
  const listedRows = (toFiniteNumber(props.modelValue.page) ?? 1) * pageSize.value
  const exactBackEndTotal = toFiniteNumber((props.modelValue as any).totalItemsBackEnd)

  if (exactBackEndTotal !== null) {
    return String(exactBackEndTotal)
  }

  const count = exactTotalRecords.value ?? totalRecords.value
  if (!Number.isFinite(count)) {
    return '0'
  }

  if (count > listedRows) {
    // Swing-style unknown total: show currently listed bound, not local estimate.
    return `${listedRows}+`
  }

  return String(count)
})

const firstRecord = computed(() => {
  const page = toFiniteNumber(props.modelValue.page) ?? 1
  return Math.max(0, (page - 1) * pageSize.value)
})

/** PrimeVue Paginator держит внутренний first — remount при внешней коррекции page/total. */
const paginatorSyncKey = computed(
  () =>
    `${props.modelValue.page ?? 1}-${props.modelValue.pages ?? 1}-${totalRecords.value}-${pageSize.value}`,
)

const rangeText = computed(() => {
  const total = totalRecords.value
  if (!total || total <= 0) {
    return '0 из 0 записей'
  }
  const from = firstRecord.value + 1
  const to = Math.min(firstRecord.value + pageSize.value, total)
  return `${from}–${to} из ${totalRecordsText.value} записей`
})

const rowCounterText = computed(() =>
  formatTableRowCounterText(totalRecordsText.value, props.selectedCount),
)

const startText = computed(() =>
  props.variant === 'summary' ? `Всего записей: ${totalRecordsText.value}` : rangeText.value,
)

const emitPagination = (newPage: number, newItemsPerPage: number) => {
  const fallbackPages = toFiniteNumber(props.modelValue.pages) ?? 1
  const fallbackItemsPerPage = toFiniteNumber(props.modelValue.itemsPerPage) ?? 10
  const totalItems =
    exactTotalRecords.value ?? Math.max(0, fallbackPages * fallbackItemsPerPage)
  const calculatedPages =
    totalItems && newItemsPerPage ? Math.ceil(totalItems / newItemsPerPage) : fallbackPages
  const safePage =
    calculatedPages > 0 ? Math.min(Math.max(1, newPage), calculatedPages) : Math.max(1, newPage)

  const newValue = {
    ...props.modelValue,
    page: safePage,
    pages: calculatedPages,
    itemsPerPage: newItemsPerPage,
    total: totalItems,
    totalItems: totalItems,
    totalItemsBackEnd:
      exactTotalRecords.value ??
      (props.modelValue as { totalItemsBackEnd?: number }).totalItemsBackEnd ??
      totalItems,
  }

  emit('update:modelValue', newValue)
  emit('change', newValue)
}

const onPageChange = (event: any) => {
  const newPage = Math.floor(event.first / event.rows) + 1
  emitPagination(newPage, event.rows)
}

const onRowsChange = (rows: number | string | null | undefined) => {
  const newItemsPerPage = toFiniteNumber(rows) ?? pageSize.value
  emitPagination(1, newItemsPerPage)
}
</script>

<style scoped lang="scss">
.m-pagination {
  width: 100%;
}

.m-pagination__start-text {
  font-size: 0.8125rem;
  color: #6b7280;
  white-space: nowrap;
}

.m-pagination__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  flex-wrap: nowrap;
}

.m-pagination__rpp {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  line-height: 1;
}

.m-pagination__rpp-label {
  display: inline-flex;
  align-items: center;
  font-size: 0.8125rem;
  color: #6b7280;
  white-space: nowrap;
  line-height: 1.625rem;
  height: 1.625rem;
}

.m-pagination__rpp-select {
  min-width: 3.25rem;
  width: auto;
  height: 1.625rem !important;
  min-height: 1.625rem !important;
  align-self: center;

  :deep(.p-select-label),
  :deep([data-pc-section='label']) {
    display: flex;
    align-items: center;
    padding: 0 0.35rem !important;
    font-size: 0.8125rem;
    min-height: 1.625rem !important;
    height: 1.625rem !important;
    line-height: 1 !important;
  }

  :deep(.p-select-dropdown),
  :deep([data-pc-section='dropdown']) {
    width: 1.5rem;
    height: 100%;
  }

  :deep(.p-select-dropdown-icon),
  :deep([data-pc-section='dropdownicon']) {
    font-size: 0.7rem;
    width: 0.7rem;
    height: 0.7rem;
  }
}

@media (max-width: 768px) {
  .m-pagination__rpp-label {
    font-size: 0.75rem;
    line-height: 1.5rem;
    height: 1.5rem;
  }

  .m-pagination__rpp-select {
    min-width: 2.75rem;
    height: 1.5rem !important;
    min-height: 1.5rem !important;

    :deep(.p-select-label),
    :deep([data-pc-section='label']) {
      padding: 0 0.25rem !important;
      min-height: 1.5rem !important;
      height: 1.5rem !important;
      font-size: 0.75rem;
    }

    :deep(.p-select-dropdown),
    :deep([data-pc-section='dropdown']) {
      width: 1.35rem;
    }
  }
}

.m-pagination--footer {
  :deep(.p-paginator),
  :deep([data-pc-name='paginator']) {
    background: transparent;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    flex-wrap: wrap;
  }

  :deep(.p-paginator-content),
  :deep([data-pc-section='content']) {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  /* PrimeVue 4: активная страница — data-p-active, не p-highlight */
  :deep(.p-paginator-page),
  :deep([data-pc-section='page']) {
    min-width: 1.75rem;
    height: 1.75rem;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #374151;
  }

  :deep(.p-paginator-page[data-p-active='true']),
  :deep([data-pc-section='page'][data-p-active='true']),
  :deep(.p-paginator-page.p-highlight),
  :deep([data-pc-section='page'].p-highlight) {
    background: var(--p-primary-color, var(--app-theme-accent, #e8751a)) !important;
    border-color: var(--p-primary-color, var(--app-theme-accent, #e8751a)) !important;
    color: #ffffff !important;
  }

  :deep(.p-paginator-prev),
  :deep(.p-paginator-next),
  :deep([data-pc-section='previous']),
  :deep([data-pc-section='next']) {
    min-width: 1.75rem;
    height: 1.75rem;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #6b7280;
  }

  :deep(.p-paginator-prev:not(:disabled):hover),
  :deep(.p-paginator-next:not(:disabled):hover),
  :deep(.p-paginator-page:not([data-p-active='true']):not(:disabled):hover) {
    background: #f9fafb;
    border-color: #d1d5db;
  }
}
</style>
